import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
type ViewType = 'listView' | 'gridView';
interface UserPrivilege {
  View: boolean;
  Search: number;
  Download: boolean;
  DownloadCount: number;
  DailySearchLimit: number;
  DailyDownloadLimit: number;
  InitialSearchCount: number;
  InitialDownloadCount: number;
  ReportLimit?: number;
  SplitDownload?: boolean;
  PageLimit?: number;
  GraphDownload?: boolean;
  GraphDownloadCount?: number;
  today_search_count?: number;
  today_download_count?: number;
}

@Injectable({
  providedIn: 'root',
})
export class CommanService {
  currentPage = new BehaviorSubject(1);
  currentRow = new BehaviorSubject(15);

  state = new BehaviorSubject({});
  currentData = new BehaviorSubject({});
  currentPrivilege = new BehaviorSubject<any>({});
  isTableSimple = new BehaviorSubject(false);
  isTableDynamic = new BehaviorSubject(false);
  isTableAdvance = new BehaviorSubject(false);
  simpleformvalue = new BehaviorSubject({});
  dynamicformvalue = new BehaviorSubject({});
  advanceformvalue = new BehaviorSubject({});

  showPageLimitWarning = new BehaviorSubject(false);
  userPageLimit = new BehaviorSubject(0);
  resetFlag = new BehaviorSubject(false);
  private currSearchQueryAsJson = new BehaviorSubject<string>('');
  productInformationData = new BehaviorSubject({});
  currentgbrn = new BehaviorSubject<any>(null);
  current_Active_Ingredient = new BehaviorSubject<any>(null);
  //technical rouute pagination
  currentPage_technical = new BehaviorSubject<number>(1);
  currentRow_technical = new BehaviorSubject(15);
  currentPage_imp = new BehaviorSubject<number>(1);
  currentRow_imp = new BehaviorSubject(15);
  currentData_technical = new BehaviorSubject({});
  currentPrivilege_technical = new BehaviorSubject({});
  showPageLimitWarning_technical = new BehaviorSubject(false);
  showPageLimitWarning_imp = new BehaviorSubject(false);
  userPageLimit_technical = new BehaviorSubject(0);
  userPageLimit_imp = new BehaviorSubject(0);
  resetFlag_technical = new BehaviorSubject(false);
  private readonly appName = 'agropat-ultimate-discovery-suite-beta';

  constructor() {
    const savedPrivilege = localStorage.getItem('user_privilege');

    if (savedPrivilege) {
      try {
        let privilege = JSON.parse(savedPrivilege);

        console.log('🔍 Loaded privilege from localStorage:', privilege);

        // Unwrap nested privilege
        privilege = this.unwrapPrivilege(privilege);

        // ---------------- PER-USER DAILY LIMIT ----------------
        const userid = this.getUserId();
        const dailyKey = `user_${userid}_daily_counts`;

        const today = new Date().toDateString();
        const savedDaily = localStorage.getItem(dailyKey);

        if (savedDaily) {
          const parsed = JSON.parse(savedDaily);

          if (parsed.date === today) {
            privilege.today_search_count = parsed.today_search_count || 0;
            privilege.today_download_count = parsed.today_download_count || 0;

            console.log(
              `📌 Restored per-user daily: search=${privilege.today_search_count}, download=${privilege.today_download_count}`
            );
          } else {
            privilege.today_search_count = 0;
            privilege.today_download_count = 0;
            console.log('🆕 New day → daily reset');
          }
        } else {
          privilege.today_search_count = 0;
          privilege.today_download_count = 0;
          console.log('📌 No daily saved → initializing to 0');
        }
        // ------------------------------------------------------

        console.log('Processed privilege:', privilege);

        this.currentPrivilege.next(privilege);
      } catch (e) {
        console.error('❌ Error loading privilege:', e);
      }
    }
  }

  private getUserId(): string {
    try {
      const auth = localStorage.getItem('currentUser');
      if (!auth) return 'guest';

      const parsed = JSON.parse(auth);
      return parsed.userId || parsed.userid || 'guest';
    } catch (e) {
      return 'guest';
    }
  }

  // 🛡️ Unwrap nested privileges
  private unwrapPrivilege(privilege: any): any {
    if (!privilege) return {};

    // Keep unwrapping while we find the appName key
    let unwrapped = privilege;
    let depth = 0;
    const maxDepth = 20; // Safety limit

    while (unwrapped && unwrapped[this.appName] && depth < maxDepth) {
      console.warn(`⚠️ Unwrapping nested privilege at depth ${depth}`);
      unwrapped = unwrapped[this.appName];
      depth++;
    }

    if (depth > 0) {
      console.error(
        `❌ Had to unwrap ${depth} levels! There's a bug somewhere.`
      );
    }

    return unwrapped;
  }

  setUserPrivilege(privilege: any) {
    console.log('🔍 setUserPrivilege - Input:', privilege);

    const unwrapped = this.unwrapPrivilege(privilege);
    console.log('✅ After unwrap:', unwrapped);

    // Get the current logged-in user ID
    const userid = this.getUserId() || 'guest';
    const dailyKey = `user_${userid}_daily_counts`;

    // --- MERGE PER USER DAILY COUNTS (CRITICAL FIX!) ---
    try {
      const today = new Date().toDateString();
      const savedDaily = localStorage.getItem(dailyKey);

      if (savedDaily) {
        const parsed = JSON.parse(savedDaily);
        if (parsed.date === today) {
          unwrapped.today_search_count = parsed.today_search_count || 0;
          unwrapped.today_download_count = parsed.today_download_count || 0;
        } else {
          unwrapped.today_search_count = 0;
          unwrapped.today_download_count = 0;
        }
      } else {
        unwrapped.today_search_count = 0;
        unwrapped.today_download_count = 0;
      }
    } catch (e) {
      unwrapped.today_search_count = 0;
      unwrapped.today_download_count = 0;
    }
    // ----------------------------------------------------

    // Send updated privilege to component
    this.currentPrivilege.next(unwrapped);

    // Save flat privilege (server version)
    localStorage.setItem('user_privilege', JSON.stringify(unwrapped));
  }

  getUserPrivilege(): Observable<any> {
    return this.currentPrivilege.asObservable();
  }

  getCurrentPrivilegeValue(): any {
    return this.currentPrivilege.getValue();
  }

  //----------------------------- Current View Type ----------------------------//
  private currViewType = new BehaviorSubject<ViewType>('gridView');
  getViewType() {
    return this.currViewType.asObservable();
  }

  setViewType(viewType: ViewType): void {
    if (viewType === 'listView' || viewType === 'gridView') {
      this.currViewType.next(viewType);
    } else {
      console.error('Error : Invalid view type passed');
    }
  }

  //----------------------------- Current breadcrumb value ----------------------------//
  private currBreadcrumbValue = new BehaviorSubject<any[]>([
    { icon: 'pi pi-home', route: '/' },
  ]);

  getCurrBreadCrumbValue() {
    return this.currBreadcrumbValue.asObservable();
  }

  // setCurrBreadCrumbValue(breadcrumbObj: breadcrumbObj): void {
  //   if (breadcrumbObj.label && breadcrumbObj.route) {
  //     let currBreadcrumb = this.currBreadcrumbValue.getValue();
  //     this.currBreadcrumbValue.next([...currBreadcrumb, breadcrumbObj]);
  //   } else {
  //     console.error('Error : Invalid breadcrumb object passed');
  //   }
  // }

  setCurrBreadCrumbValue(breadcrumbObj: {
    label: string;
    route: string;
  }): void {
    if (breadcrumbObj.label && breadcrumbObj.route) {
      let currBreadcrumb = this.currBreadcrumbValue.getValue();

      // Check if the new breadcrumb already exists
      const isDuplicate = currBreadcrumb.some(
        (item) =>
          item.label === breadcrumbObj.label &&
          item.route === breadcrumbObj.route
      );

      if (!isDuplicate) {
        this.currBreadcrumbValue.next([...currBreadcrumb, breadcrumbObj]);
      }
    } else {
      console.error('Error : Invalid breadcrumb object passed');
    }
  }

  removeLastBreadCrumbValue() {
    let currBreadcrumb = this.currBreadcrumbValue.getValue();
    if (currBreadcrumb.length > 1) {
      this.currBreadcrumbValue.next(currBreadcrumb.slice(0, -1));
    }
  }

  resetCurrBreadCrumbValue() {
    this.currBreadcrumbValue.next([{ icon: 'pi pi-home', route: '/' }]);
  }

  //----------------------------- Data for Preview ----------------------------//
  private currResultData = new BehaviorSubject<any[]>([]);
  private currResultTotal = new BehaviorSubject<number>(0);
  private currResultCountries = new BehaviorSubject<any[]>([]);
  private currSelectedSubFilterCountry = new BehaviorSubject<string>('');
  private currResultCompanies = new BehaviorSubject<any[]>([]);
  private currSelectedSubFilterCompany = new BehaviorSubject<string>('');
  private currSearchQueryAsString = new BehaviorSubject<string>('');

  // Methods for currResultData
  setResultData(data: any[]): void {
    this.currResultData.next(data);
  }

  getResultData() {
    return this.currResultData.asObservable();
  }

  // Methods for currResultTotal
  setResultTotal(total: number): void {
    this.currResultTotal.next(total);
  }

  getResultTotal() {
    return this.currResultTotal.asObservable();
  }

  // Methods for currResultCountries
  setResultCountries(countries: any[]): void {
    this.currResultCountries.next(countries);
  }

  getResultCountries() {
    return this.currResultCountries.asObservable();
  }

  // Methods for currSelectedSubFilterCountry
  setSelectedSubFilterCountry(subFilterCountry: string): void {
    this.currSelectedSubFilterCountry.next(subFilterCountry);
  }

  getSelectedSubFilterCountry() {
    return this.currSelectedSubFilterCountry.asObservable();
  }

  // Methods for currResultCompanies
  setResultCompanies(companies: any[]): void {
    this.currResultCompanies.next(companies);
  }

  getResultCompanies() {
    return this.currResultCompanies.asObservable();
  }

  // Methods for currSelectedCompany
  setSelectedSubFilterCompany(subFiltercompany: string): void {
    this.currSelectedSubFilterCompany.next(subFiltercompany);
  }

  getSelectedSubFilterCompany() {
    return this.currSelectedSubFilterCompany.asObservable();
  }

  // Methods for currSearchQueryAsString
  setSearchQueryAsStringy(searchQuery: string): void {
    this.currSearchQueryAsString.next(searchQuery);
  }

  getSearchQueryAsString() {
    return this.currSearchQueryAsString.asObservable();
  }

  getSearchQueryAsString_normal() {
    return this.currSearchQueryAsString.getValue();
  }

  // Methods for currSearchQueryAsString
  setSearchQueryAsJson(searchQuery: string): void {
    this.currSearchQueryAsJson.next(searchQuery);
  }

  getSearchQueryAsJson() {
    return this.currSearchQueryAsJson.asObservable();
  }

  getSearchQueryAsJson_normal() {
    return this.currSearchQueryAsJson.getValue();
  }
  //----------------------------- Columns ----------------------------//
  private currColumns = new BehaviorSubject<any[]>([]);

  // Methods for currColumns
  setColumns(data: any[]): void {
    this.currColumns.next(data);
  }

  getColumns() {
    return this.currColumns.asObservable();
  }

  getColumns_normal() {
    return this.currColumns.getValue();
  }

  //----------------------------- Table subject ----------------------------//
  private currPageNumber$ = new BehaviorSubject<number>(1);
  private currLimit$ = new BehaviorSubject<number>(10);
  private currSortField$ = new BehaviorSubject<string>('');
  private currSortOrder$ = new BehaviorSubject<string>('ASC');
  private currFilter$ = new BehaviorSubject<any>({});

  // Page Number
  setPageNumber(page: number): void {
    this.currPageNumber$.next(page);
  }

  getPageNumber() {
    return this.currPageNumber$.asObservable();
  }

  // Limit
  setLimit(limit: number): void {
    this.currLimit$.next(limit);
  }

  getLimit() {
    return this.currLimit$.asObservable();
  }

  // Sort Field
  setSortField(field: string): void {
    this.currSortField$.next(field);
  }

  getSortField() {
    return this.currSortField$.asObservable();
  }

  // Sort Order
  setSortOrder(order: string): void {
    this.currSortOrder$.next(order);
  }

  getSortOrder() {
    return this.currSortOrder$.asObservable();
  }

  // Filter
  setFilter(filter: any): void {
    this.currFilter$.next(filter);
  }

  getFilter() {
    return this.currFilter$.asObservable();
  }

  //discovery
  currColumns$ = new BehaviorSubject<any[]>([]);

  pageLimitInPrivilegeJson$ = new BehaviorSubject<number>(0);
  pageLimitInDatabaseLimit$ = new BehaviorSubject<number>(0);
}
